/**
 * ProjectionsModal.tsx – 5-year projections for aged care strategy: an accordion of charts
 * (cashflow, net worth, debt) over a table of net worth, pension, care costs and cashflow.
 * Used by AgedCareAnalyser (in App.js). Props: onClose, projections, agedCareCosts, strategy, factFind, initialFinancials, currentFinancials, onPeriodModeChange, isReloading.
 *
 * Two toggles sit in the header, and BOTH are the modal's own sticky state — remembered per device
 * in projectionsModalPreferences, not passed in, so they survive closing the modal and reloading the
 * app. "Future / today's dollars" is purely presentational: the modal deflates the rows it already
 * has. "Financial year / calendar year" is not — the rows themselves are cut differently (and the
 * engine runs its plugin phases in a different order), so flipping it tells the owner to re-run the
 * projection and shows an hourglass over the charts and table until the new rows land.
 */
import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, HelpCircle, ChevronRight, ChevronDown, Hourglass } from 'lucide-react';
import { formatCurrency } from '../../utils/index.ts';
import { ASSET_TYPES } from '../../constants/index.ts';
import { CalculationTooltip } from '../../components/ui/index.tsx';
import ProjectionChartsAccordion from './ProjectionChartsAccordion.tsx';
import { scaleChartPoint, type ChartPoint } from '../../components/charts/chartTypes.ts';
import { buildCashflowChartYears } from './cashflowChartData.ts';
import type { AgedCareCosts } from '../../../../shared/src/calculation/agedCareCostTypes.ts';
import { buildDebtChartYears, buildNetWorthChartYears } from './balanceSheetChartData.ts';
import { PROJECTION_BREAKDOWN_CLASS, PROJECTION_BREAKDOWN_CSS } from './projectionBreakdownStyles.ts';
import { describeProjectionPeriod } from '../../../../shared/src/calculation/projectionPeriodDescription.ts';
import {
  readPeriodMode,
  readTodaysDollars,
  writePeriodMode,
  writeTodaysDollars,
  type ProjectionsModalPeriodMode,
} from './projectionsModalPreferences.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

// The calc engine's per-year projection row — REST response shape (postCalc returns Promise<any> by
// design, see calculationApi.ts), reconstructed here as a loose interface with the fields this modal
// actually reads. `[key: string]: unknown` covers every other field the engine attaches.
interface ProjectionBreakdownItem {
  label: string;
  value: string | number;
}

interface ProjectionAssetRow {
  name?: string;
  description?: string;
  type?: string;
  balance?: number;
  value?: number;
  residualValue?: number;
  [key: string]: unknown;
}

interface ProjectionLiabilityRow {
  description?: string;
  type?: string;
  balance?: number;
  [key: string]: unknown;
}

// The named buckets are the ones this modal's own breakdowns read; the index signature covers every
// other fact-find category (and anything a strategy injects), which the balance-sheet charts total.
interface ProjectionYearAssets {
  bankAccounts?: ProjectionAssetRow[];
  shares?: ProjectionAssetRow[];
  accountBasedPensions?: ProjectionAssetRow[];
  radPaid?: ProjectionAssetRow[];
  [key: string]: ProjectionAssetRow[] | undefined;
}

interface ProjectionYear {
  netWorth?: number;
  netWorthBreakdown?: ProjectionBreakdownItem[];
  socialSecurity?: number;
  socialSecurityBreakdown?: ProjectionBreakdownItem[];
  investmentIncome?: number;
  abpIncome?: number;
  /** Salary component of the year's other income — lets the cashflow chart show salary as its own bar segment. */
  salaryIncome?: number;
  rentalIncome?: number;
  annualCareCost?: number;
  agedCareBreakdown?: ProjectionBreakdownItem[];
  livingExpenses?: number;
  tax?: number;
  cashFlow?: number;
  capitalExpenses?: number;
  cashFlowBreakdown?: ProjectionBreakdownItem[];
  /** Human-readable HTML breakdown of the year's calculations (see server `ProjectionYearResult.breakdown`). */
  breakdown?: string;
  /** CPI assumption (percent) used for this projection — constant across years; drives the "today's dollars" deflation. */
  inflationPct?: number;
  cashflowRoutingLine?: ProjectionBreakdownItem;
  fyLabel?: string;
  isPartialFy?: boolean;
  /** Share of a full year this row covers — 1 for a full row, e.g. 148/365 for one opening on 6 August. */
  periodYearFraction?: number;
  /**
   * The same figures as their un-suffixed siblings, scaled to the days this row actually covers. The
   * engine sets them ONLY on a partial row, which is what makes `partial ?? annual` the whole rule.
   *
   * The fields above are ANNUAL RATES, which is right for a full row and wrong for a partial one: a
   * row covering 6 August → 31 December was reporting a full year's pension and a full year's care
   * fees beside a balance sheet that had only moved by the period's share, so the two halves of one
   * row disagreed. These are what a partial row shows.
   */
  socialSecurityForPartialPeriod?: number;
  /** The pension actually deposited across this row's days — see the projection engine's row type. */
  agePensionPaidForPeriod?: number;
  /** The entitlement rate as at the row's LAST payday — where the fortnightly re-assessments finished. */
  socialSecurityAtPeriodEnd?: number;
  annualCareCostForPartialPeriod?: number;
  cashFlowForPartialPeriod?: number;
  livingExpensesForPartialPeriod?: number;
  taxForPartialPeriod?: number;
  investmentIncomeForPartialPeriod?: number;
  periodStart?: string;
  periodEnd?: string;
  home?: { value?: number };
  assets?: ProjectionYearAssets;
  liabilities?: Record<string, ProjectionLiabilityRow[] | undefined>;
  /** The two strategy loans, tracked outside `liabilities` — see the engine's ProjectionYearResult. */
  reverseMortgageLoanBalance?: number;
  heasLoanBalance?: number;
  [key: string]: unknown;
}

type FinancialsSummary = { netWorth?: number } & Record<string, unknown>;

export interface ProjectionsViewProps {
  /** Renders the header's close button when provided (the overlay modal does; an embedding tab doesn't). */
  onClose?: () => void;
  projections: ProjectionYear[];
  agedCareCosts?: AgedCareCosts | null;
  // Unused by this view's own rendering (kept for parity with the caller's strategy-details object).
  strategy?: unknown;
  /**
   * The OPENING state for year 1 — post-strategy, so the year-1 asset tooltip describes the position
   * the projection actually started from (the manual control passes its simulated form data; the
   * Suggestion Engine deep dive passes the scenario's post-RAD-funding equivalent).
   */
  factFind: DraftFactFind;
  initialFinancials?: FinancialsSummary | null;
  currentFinancials?: FinancialsSummary | null;
  /**
   * Asks the owner to re-run the projection on the other calendar (a REST call, hence `isReloading`).
   * The mode itself is the view's sticky preference, so the owner reads the same store to know which
   * rows to supply on open — see projectionsModalPreferences. OPTIONAL: an embedder with no re-run
   * pathway omits it, which hides the financial/calendar toggle and pins the view to the financial
   * years its rows were cut on — the sticky preference must not mislabel rows nobody can re-cut.
   */
  onPeriodModeChange?: (mode: ProjectionsModalPeriodMode) => void;
  /** True while that re-run is in flight — the view covers the charts and table with an hourglass. */
  isReloading?: boolean;
  /**
   * The recurring-cost column's heading, or `null` to hide the column entirely.
   *
   * This view is now the ONE projections surface for every module — Aged Care, Support at Home,
   * Retirement Village, Granny Flat and Downsizer — because they all run the same engine and their
   * rows carry the same charts data and monthly narrative. The only real difference between them is
   * what the recurring cost IS: aged care fees, a Support at Home contribution, village fees, or (for
   * a granny flat or downsize) nothing at all. Defaults to 'Aged Care Costs'.
   */
  costLabel?: string | null;
  /** Which engine field the cost column reads: 'care' (annualCareCost, default) or 'village' (annualVillageCost). */
  costSource?: 'care' | 'village';
}

interface ProjectionsModalProps extends ProjectionsViewProps {
  onClose: () => void;
}

/**
 * The day a partial period opened, as `6 Aug` — what a row's grey sub-line names.
 *
 * Read with LOCAL getters: the engine serialises a local-midnight date with `toISOString()`, which
 * moves it to the previous UTC day anywhere east of Greenwich, so slicing the string would report
 * 5 August for a projection that opened on the 6th.
 */
export function periodOpenedOn(periodStart: string | undefined): string | null {
  if (!periodStart) return null;
  const date = new Date(periodStart);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

/**
 * The two lines that close a PARTIAL row's tooltip, and nothing at all for a full row.
 *
 * The breakdowns the engine sends (`socialSecurityBreakdown`, `agedCareBreakdown`,
 * `cashFlowBreakdown`) are worked at ANNUAL rates — that is what the means test and the fee schedule
 * produce, and it is the right thing to show a reader the workings in. The headline beside them is now
 * the period's own share, so without these two lines the tooltip would appear not to add up. They name
 * the annual rate the lines above describe, then the share of it this row covers.
 */
export function periodScalingLines(
  year: ProjectionYear,
  annualRate: number,
  forPeriod: number,
  formatCurrencyFn: (value: number) => string,
): ProjectionBreakdownItem[] {
  if (!year.isPartialFy || Math.abs(annualRate - forPeriod) < 1) return [];
  // Days rather than a bare fraction: "148 of 365 days" is checkable against the dates in the row's
  // own title, where "0.4055 of a year" is not.
  const days = Math.round((year.periodYearFraction ?? 1) * 365);
  return [
    { label: '---', value: '' },
    { label: 'Annual rate (the lines above)', value: formatCurrencyFn(annualRate) },
    { label: `This period (${days} of 365 days)`, value: formatCurrencyFn(forPeriod) },
  ];
}

/**
 * The lines that close the AGE PENSION tooltip, replacing the generic period-scaling pair.
 *
 * The detailed lines above them are the OPENING assessment — the full means test as the year began,
 * which is the right level of workings for an adviser. But the headline beside the tooltip is the
 * money actually paid, and the two part company whenever the entitlement moved mid-year: a RAD paid on
 * entry day takes several hundred thousand dollars out of the assets test, and every later fortnight
 * pays the higher rate. Without these lines the tooltip explained a rate the headline visibly did not
 * match — an adviser checking the workings would conclude the figures do not reconcile, when they do.
 *
 * Fires on the rate MOVING, not on the year being partial: a full financial year with a mid-year entry
 * has exactly the same gap.
 */
export function pensionRateMovementLines(
  year: ProjectionYear,
  paid: number,
  formatCurrencyFn: (value: number) => string,
): ProjectionBreakdownItem[] {
  const opening = year.socialSecurity ?? 0;
  const closing = year.socialSecurityAtPeriodEnd ?? opening;
  // No material movement: keep the ordinary partial-year scaling lines (or nothing on a full year).
  if (Math.abs(closing - opening) < 1) return periodScalingLines(year, opening, paid, formatCurrencyFn);
  const days = Math.round((year.periodYearFraction ?? 1) * 365);
  return [
    { label: '---', value: '' },
    { label: 'Rate at start of year (the lines above)', value: `${formatCurrencyFn(opening)} p.a.` },
    { label: 'Rate by end of year', value: `${formatCurrencyFn(closing)} p.a.` },
    // Plain words for the consumer; the adviser has the two rates to check against a Centrelink letter.
    { label: 'Why it moved', value: 'Re-assessed each fortnight as assets and income changed during the year' },
    { label: `Paid this period (${days} of 365 days)`, value: formatCurrencyFn(paid) },
  ];
}

/**
 * When `year.cashFlowBreakdown` is missing, split total investment income into non-ABP vs ABP for
 * reconciliation. `periodShare` scales both to the days the row covers (1 for a full row), so these
 * lines add up to the headline figure beside them rather than to a full year's.
 */
function buildFallbackInvestmentIncomeLines(year: ProjectionYear, periodShare: number, formatCurrencyFn: (value: number) => string): ProjectionBreakdownItem[] {
  const total = (year.investmentIncomeForPartialPeriod ?? year.investmentIncome ?? 0);
  const abp = (year.abpIncome ?? 0) * periodShare;
  const other = Math.max(0, total - abp);
  const lines: ProjectionBreakdownItem[] = [];
  if (other > 0) {
    lines.push({ label: 'Investment Income', value: '+' + formatCurrencyFn(other) });
  }
  if (abp > 0) {
    lines.push({ label: 'ABP Income', value: '+' + formatCurrencyFn(abp) });
  }
  if (lines.length === 0 && total > 0) {
    lines.push({ label: 'Investment Income', value: '+' + formatCurrencyFn(total) });
  }
  return lines;
}

export const ProjectionsView = ({ onClose, projections, agedCareCosts, factFind, initialFinancials, currentFinancials, onPeriodModeChange, isReloading = false, costLabel = 'Aged Care Costs', costSource = 'care' }: ProjectionsViewProps) => {
  // The cost column is present unless a module said it has no recurring cost at all.
  const showCostColumn = costLabel !== null;
  const costHeading = costLabel ?? '';
  // Both toggles open on whatever the user last chose (see projectionsModalPreferences). Lazy
  // initialisers, so localStorage is read once per open rather than on every render. Without a
  // re-run pathway the rows are financial-year cuts and stay that way, whatever the sticky says.
  const [periodMode, setPeriodMode] = useState<ProjectionsModalPeriodMode>(() => (onPeriodModeChange ? readPeriodMode() : 'financial_year'));
  // false = show nominal "future dollars" (default); true = deflate to real "today's dollars".
  const [todaysDollars, setTodaysDollars] = useState(readTodaysDollars);
  const calendarYears = periodMode === 'calendar_year';
  // Wording that follows the period the rows are cut on: a calendar row closes on 31 December, an
  // Australian financial year on 30 June. Shared with both SOAs, so a document can never name a
  // period the numbers underneath it were not cut on.
  const period = describeProjectionPeriod(periodMode);
  const periodNoun = period.noun;
  const periodCloseDate = period.closesOn;
  const [projectionsIntroExpanded, setProjectionsIntroExpanded] = useState(false);
  /** Remember the choice, then ask the owner for rows cut the new way — it, not the view, runs the engine. */
  const changePeriodMode = (mode: ProjectionsModalPeriodMode) => {
    if (!onPeriodModeChange) return; // no re-run pathway => the toggle isn't rendered
    setPeriodMode(mode);
    writePeriodMode(mode);
    onPeriodModeChange(mode);
  };
  const changeTodaysDollars = (next: boolean) => {
    setTodaysDollars(next);
    writeTodaysDollars(next);
  };
  // Which years' human-readable HTML breakdowns are expanded (see `ProjectionYear.breakdown`).
  const [breakdownExpanded, setBreakdownExpanded] = useState<Record<number, boolean>>({});
  const toggleBreakdown = (index: number) =>
    setBreakdownExpanded((prev) => ({ ...prev, [index]: !prev[index] }));

  // CPI is constant across the projection (server sets it once); fall back to the engine's 2.5% default.
  const inflationPct = projections.find((y) => y.inflationPct != null)?.inflationPct ?? 2.5;
  // "Today's dollars": deflate a year's nominal value by inflation compounded over the number of years
  // since the first projection row. Year index 0 ("Comparison Yr1" / current year) is never deflated,
  // so the toggle has no effect on first-year values.
  const isConverted = (yearIndex: number) => todaysDollars && yearIndex > 0;
  const deflateFactor = (yearIndex: number) =>
    isConverted(yearIndex) ? 1 / Math.pow(1 + inflationPct / 100, yearIndex) : 1;
  const deflateAmount = (value: number, yearIndex: number) => value * deflateFactor(yearIndex);
  /** Deflate every `$1,234` / `-$1,234` occurrence inside an arbitrary string (e.g. a pre-formatted breakdown value). */
  const deflateCurrencyString = (str: string, yearIndex: number): string => {
    const factor = deflateFactor(yearIndex);
    if (factor === 1) return str;
    return str.replace(/-?\$[\d,]+/g, (match) => {
      const negative = match.startsWith('-');
      const numeric = parseFloat(match.replace(/[-$,]/g, '')) || 0;
      return formatCurrency((negative ? -numeric : numeric) * factor);
    });
  };
  /** Deflate a tooltip breakdown's rows: numeric values numerically, pre-formatted string values by scanning for `$` amounts. */
  const deflateRows = (rows: ProjectionBreakdownItem[], yearIndex: number): ProjectionBreakdownItem[] =>
    deflateFactor(yearIndex) === 1
      ? rows
      : rows.map((row) => ({
          ...row,
          value: typeof row.value === 'number' ? deflateAmount(row.value, yearIndex) : deflateCurrencyString(row.value, yearIndex),
        }));
  // Blue text signals a value that has been converted to today's dollars; otherwise keep the base colour.
  const moneyClass = (yearIndex: number, baseColorClass: string) => (isConverted(yearIndex) ? 'text-blue-600' : baseColorClass);
  /** Render a year's breakdown HTML, replacing `<nv>` nominal-value tags: converted → deflated blue span; otherwise the plain amount. */
  const renderBreakdownHtml = (html: string, yearIndex: number): string =>
    isConverted(yearIndex)
      ? html.replace(/<nv>(.*?)<\/nv>/g, (_full, inner: string) => `<span class="text-blue-600 font-semibold">${deflateCurrencyString(inner, yearIndex)}</span>`)
      : html.replace(/<\/?nv>/g, '');

  // The chart accordion's three sets of points, deflated per year exactly like the table below them
  // so the today's-dollars toggle moves both together. Built in one memo — `deflateFactor` above is
  // rebuilt every render, so a helper closing over it would have to be excluded from the deps.
  const chartPoints = useMemo(() => {
    const deflate = (points: ChartPoint[]): ChartPoint[] =>
      points.map((point, index) => scaleChartPoint(point, todaysDollars && index > 0 ? 1 / Math.pow(1 + inflationPct / 100, index) : 1));
    return {
      cashflow: deflate(buildCashflowChartYears(projections, (agedCareCosts?.totalDailyCost || 0) * 365)),
      netWorth: deflate(buildNetWorthChartYears(projections)),
      debt: deflate(buildDebtChartYears(projections)),
    };
  }, [projections, agedCareCosts, todaysDollars, inflationPct]);

  const createAssetBreakdown = (yearIndex: number): ProjectionBreakdownItem[] => {
    const year = projections[yearIndex];
    const breakdown: ProjectionBreakdownItem[] = [];

    if (yearIndex === 0) {
      if (factFind.home?.value) {
        breakdown.push({ label: 'Family Home', value: formatCurrency(Number(factFind.home.value)) });
      }
      ASSET_TYPES.forEach(type => {
        const assetList = factFind.assets?.[type.id];
        if (type.isLiquid && assetList) {
          assetList.forEach((asset, index) => {
            const rawValue = asset.balance != null ? asset.balance : asset.value;
            const numeric = rawValue || 0;
            if (numeric > 0) {
              breakdown.push({
                label: `${(asset as { name?: string }).name || asset.description || type.name} ${index + 1}`,
                value: formatCurrency(numeric)
              });
            }
          });
        }
      });
      const radPaidAssets = factFind.assets?.radPaid;
      if (radPaidAssets && radPaidAssets.length > 0) {
        radPaidAssets.forEach((rad, index) => {
          const val = parseFloat(String(rad.value)) || 0;
          if (val > 0) {
            breakdown.push({ label: `RAD Paid ${index + 1}`, value: formatCurrency(val) });
          }
        });
      }
    } else if (year?.assets) {
      if (year.home?.value) {
        breakdown.push({ label: 'Family Home', value: formatCurrency(year.home.value) });
      }
      if (year.assets.bankAccounts && year.assets.bankAccounts.length > 0) {
        year.assets.bankAccounts.forEach((account, index) => {
          if ((account.balance || 0) > 0) {
            breakdown.push({ label: `${account.name || 'Bank Account'} ${index + 1}`, value: formatCurrency(account.balance || 0) });
          }
        });
      }
      if (year.assets.shares && year.assets.shares.length > 0) {
        year.assets.shares.forEach((share, index) => {
          if ((share.value || 0) > 0) {
            breakdown.push({ label: `${share.name || 'Shares'} ${index + 1}`, value: formatCurrency(share.value || 0) });
          }
        });
      }
      if (year.assets.accountBasedPensions && year.assets.accountBasedPensions.length > 0) {
        year.assets.accountBasedPensions.forEach((abp, index) => {
          if ((abp.value || 0) > 0) {
            breakdown.push({ label: `${abp.description || 'ABP'} ${index + 1}`, value: formatCurrency(abp.value || 0) });
          }
        });
      }
      if (year.assets.radPaid && year.assets.radPaid.length > 0) {
        year.assets.radPaid.forEach((rad, index) => {
          breakdown.push({ label: `RAD Paid ${index + 1}`, value: formatCurrency(rad.value || 0) });
        });
      }
    }

    const openingBalance = yearIndex === 0
      ? (currentFinancials?.netWorth || initialFinancials?.netWorth || 0)
      : (projections[yearIndex - 1]?.netWorth || 0);
    breakdown.push({ label: '=== TOTAL NET WORTH ===', value: formatCurrency(openingBalance) });
    return breakdown;
  };

  const createAgePensionBreakdown = (yearIndex: number, agePension: number): ProjectionBreakdownItem[] => {
    const year = projections[yearIndex];
    const breakdown: ProjectionBreakdownItem[] = [];
    if (year?.socialSecurityBreakdown && Array.isArray(year.socialSecurityBreakdown)) {
      year.socialSecurityBreakdown.forEach(item => {
        breakdown.push({
          label: item.label,
          value: typeof item.value === 'number' ? formatCurrency(item.value) : item.value
        });
      });
    } else {
      breakdown.push({ label: 'Age Pension', value: formatCurrency(agePension) });
      breakdown.push({ label: 'Family Home Status', value: yearIndex < 2 ? 'Exempt (first 2 years)' : 'Assessable (after 2 years)' });
      breakdown.push({ label: 'Asset Test Impact', value: yearIndex < 2 ? 'Home exempt from asset test' : 'Home included in asset test' });
    }
    return breakdown;
  };

  return (
    <>
        <div className="px-4 sm:px-6 pt-5 sm:pt-6 pb-4 border-b border-slate-100 bg-gradient-to-br from-slate-50/90 via-white to-teal-50/40">
          {/* Wraps rather than overflows: on a narrow window the two toggles and the close button
              drop to their own line — and then wrap against each other — instead of the close button
              being pushed off the right edge. */}
          <div className="flex flex-wrap justify-between items-center gap-x-4 gap-y-2">
            <h3 className="min-w-0 text-xl sm:text-2xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-sky-500 to-teal-500">{projections.length} Year Projections</h3>
            <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2 ml-auto">
              {/* Unlike the dollars toggle beside it, this one re-runs the projection engine — hence
                  the disabled state while a run is in flight, and the hourglass over the content.
                  Hidden entirely when the embedder has no way to re-cut the rows. */}
              {onPeriodModeChange ? (
              <div className="flex items-center gap-2 text-sm font-medium">
                <span className={calendarYears ? 'text-slate-400' : 'text-slate-700'}>Financial year</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={calendarYears}
                  aria-label={calendarYears ? 'Showing calendar years' : 'Showing financial years'}
                  disabled={isReloading}
                  onClick={() => changePeriodMode(calendarYears ? 'financial_year' : 'calendar_year')}
                  title={calendarYears
                    ? 'Showing calendar years: each row runs 1 January to 31 December. Switch left for Australian financial years.'
                    : 'Showing Australian financial years: each row runs 1 July to 30 June. Switch right for calendar years.'}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors disabled:cursor-wait disabled:opacity-60 ${
                    calendarYears ? 'border-teal-300 bg-teal-500' : 'border-slate-300 bg-slate-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                      calendarYears ? 'translate-x-6' : 'translate-x-1'
                    }`}
                    aria-hidden
                  />
                </button>
                <span className={calendarYears ? 'text-teal-700' : 'text-slate-400'}>Calendar year</span>
              </div>
              ) : null}
              <div className="flex items-center gap-2 text-sm font-medium">
                <span className={todaysDollars ? 'text-slate-400' : 'text-slate-700'}>Future dollars</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={todaysDollars}
                  aria-label={todaysDollars ? "Showing today's dollars" : 'Showing future dollars'}
                  onClick={() => changeTodaysDollars(!todaysDollars)}
                  title={todaysDollars
                    ? "Showing today's dollars: future values are deflated by inflation so they compare with money today. Switch left for future dollars."
                    : 'Showing future dollars (nominal): the actual amounts in each future year. Switch right for today’s dollars.'}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors ${
                    todaysDollars ? 'border-blue-300 bg-blue-500' : 'border-slate-300 bg-slate-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
                      todaysDollars ? 'translate-x-6' : 'translate-x-1'
                    }`}
                    aria-hidden
                  />
                </button>
                <span className={todaysDollars ? 'text-blue-700' : 'text-slate-400'}>Today&apos;s dollars</span>
              </div>
              {onClose ? (
                <button onClick={onClose} className="shrink-0 rounded-lg p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors" aria-label="Close projections modal"><X /></button>
              ) : null}
            </div>
          </div>
          <div className="h-1 w-full rounded-full bg-gradient-to-r from-blue-400 via-teal-400 to-cyan-400 mt-4 opacity-90" aria-hidden />
        </div>
        {/* The positioning context for the busy overlay is this NON-scrolling wrapper, not the
            scroller inside it — anchored to the scroller, the overlay would sit at the top of the
            content and scroll away rather than staying over the visible charts and table. */}
        <div className="relative flex-1 min-h-0 flex">
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 w-full min-w-0">
            <div className={`space-y-4 w-full max-w-none ${isReloading ? 'pointer-events-none' : ''}`}>
              <ProjectionChartsAccordion cashflow={chartPoints.cashflow} netWorth={chartPoints.netWorth} debt={chartPoints.debt} formatValue={formatCurrency} />
              <div className="flex flex-wrap items-center justify-between gap-2 gap-y-1">
                <button
                  type="button"
                  onClick={() => setProjectionsIntroExpanded((v) => !v)}
                  className="inline-flex items-center gap-1.5 shrink-0 rounded-lg px-2 py-1 text-sm font-medium text-teal-800 hover:bg-teal-50 hover:text-teal-950 transition-colors"
                  aria-expanded={projectionsIntroExpanded}
                  aria-controls="projections-five-year-intro"
                  id="projections-five-year-intro-toggle"
                >
                  {projectionsIntroExpanded ? (
                    <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
                  ) : (
                    <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
                  )}
                  How to read this
                </button>
              </div>
              {projectionsIntroExpanded ? (
                <div
                  id="projections-five-year-intro"
                  role="region"
                  aria-labelledby="projections-five-year-intro-toggle"
                  className="w-full max-w-none rounded-lg border border-slate-200/90 bg-slate-50/70 px-3 py-2.5"
                >
                  <p className="text-sm text-slate-600 leading-relaxed">
                    These projections are <strong>illustrative only</strong>. They can change with the timing of your strategy, investment returns, and future threshold or rule changes.
                  </p>
                  {/* The arrival-side twin of the "Annual Position vs View Projections" explainer on the
                      Financial Impact panel, in the same terms, for the reader who lands here first
                      and wonders why year 1 matches neither panel figure. */}
                  <p className="text-sm text-slate-600 leading-relaxed mt-2">
                    Each row shows what that year <strong>actually pays</strong>. The Financial Impact panel shows a <strong>normalised year</strong> instead — your plan&apos;s full 12-month impact as if it were in place today — so the year the plan starts will sit between the panel&apos;s before and after figures.
                  </p>
                  <p className="text-sm text-slate-600 leading-relaxed mt-2">
                    Each row is named for the {periodNoun} it covers. Where your projection starts part-way through one, the row says so and every figure on it — pension, care costs, cashflow and the balance sheet — covers only the days from that date to {periodCloseDate}. Open a figure&apos;s tooltip to see the annual rate it was worked from and the share of it this period carries.
                  </p>
                  <p className="text-sm text-slate-600 leading-relaxed mt-2">
                    {calendarYears ? (
                      <>
                        Rows are cut on <strong>calendar years</strong> (1 January to 31 December), per the toggle above, which is also how the modelling runs — growth is applied on 1 January, and the Age Pension, care fees and the closing balance sheet are struck on 31 December — so nothing is blended or estimated between periods. The one thing that does not move is the law: Australian tax and Centrelink years run 1 July to 30 June, so the tax on a calendar row is assessed over that calendar year for modelling purposes and will not match a tax return. The calculation breakdown shows every event on the real date it happened.
                      </>
                    ) : (
                      <>
                        Rows are cut on <strong>financial years</strong> (1 July to 30 June), per the toggle above, which is also how the modelling runs — so each row&apos;s figures are that financial year&apos;s, with nothing blended or estimated between periods.
                      </>
                    )}
                  </p>
                  {/* The dates inside an expanded row are the single most-asked-about thing here: three
                      different rhythms overlaid looks arbitrary until you know there are three. */}
                  <p className="text-sm text-slate-600 leading-relaxed mt-2">
                    Open a row and you&apos;ll see several dates each month. That&apos;s three separate rhythms running at once. <strong>The 1st of each month</strong> is when growth is applied to the home and to investments — monthly rather than once a year, so a mid-year event lands on a realistic balance. <strong>Every fortnight</strong> the Age Pension arrives and living expenses come out, because that is how they are actually received and spent. <strong>Aged care fees</strong> come out monthly, from the day care starts. Anything with a date of its own — a birthday, moving into care, selling an asset — appears on that date. The last day of the {periodNoun} is where the year is squared up: any shortfall is funded and the closing balance sheet is struck.
                  </p>
                  <p className="text-sm text-slate-600 leading-relaxed mt-2">
                    <strong>The exact days are illustrative.</strong> Centrelink sets each person&apos;s own pension payday, so yours will very likely fall on a different day of the fortnight to the one shown. Super and account-based pension payments depend on what you and your provider have arranged — monthly, quarterly or annually — and aged care providers invoice on their own cycles. We use regular, evenly-spaced dates so the picture is realistic and consistent; the <em>amounts</em> and the totals for each year are what these projections are for, not the precise day money moves.
                  </p>
                </div>
              ) : null}
              {/* Once for every expanded year's breakdown below. A stylesheet rather than utility
                  classes because the design is pseudo-element markers in a gutter, and because the
                  marker colours are generated from the accordion's own chart series. */}
              <style>{PROJECTION_BREAKDOWN_CSS}</style>
              {/* The table has a 900px floor (six money columns don't compress), so the wrapper must
                  scroll horizontally — `overflow-hidden` silently cut the closing-net-worth column off
                  on any window narrower than that, with no way to reach it. */}
              <div className="rounded-xl border border-slate-200 overflow-x-auto shadow-sm" data-testid="projections-table-scroller">
                <table className="w-full text-sm border-collapse min-w-[900px]">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold border-b border-slate-200/90 bg-gradient-to-r from-blue-50 via-sky-50/80 to-teal-50 text-slate-800 uppercase tracking-wider text-xs">Year</th>
                      <th className="px-4 py-3 text-left font-semibold border-b border-slate-200/90 bg-gradient-to-r from-blue-50 via-sky-50/80 to-teal-50 text-slate-800 uppercase tracking-wider text-xs">Net Worth (Opening)</th>
                      <th className="px-4 py-3 text-left font-semibold border-b border-slate-200/90 bg-gradient-to-r from-blue-50 via-sky-50/80 to-teal-50 text-slate-800 uppercase tracking-wider text-xs">Age Pension</th>
                      {showCostColumn && (
                        <th className="px-4 py-3 text-left font-semibold border-b border-slate-200/90 bg-gradient-to-r from-blue-50 via-sky-50/80 to-teal-50 text-slate-800 uppercase tracking-wider text-xs">{costHeading}</th>
                      )}
                      <th className="px-4 py-3 text-left font-semibold border-b border-slate-200/90 bg-gradient-to-r from-blue-50 via-sky-50/80 to-teal-50 text-slate-800 uppercase tracking-wider text-xs">Net Cashflow</th>
                      <th className="px-4 py-3 text-left font-semibold border-b border-slate-200/90 bg-gradient-to-r from-blue-50 via-sky-50/80 to-teal-50 text-slate-800 uppercase tracking-wider text-xs">Net Worth (Closing)</th>
                      <th className="px-2 py-3 border-b border-slate-200/90 bg-gradient-to-r from-blue-50 via-sky-50/80 to-teal-50 w-10" aria-label="Calculation breakdown" />
                    </tr>
                  </thead>
                  <tbody>
                    {projections.map((year, index) => {
                      const openingBalance = index === 0 ? (currentFinancials?.netWorth || initialFinancials?.netWorth || 0) : (projections[index - 1]?.netWorth || 0);
                      const closingBalance = year.netWorth || 0;
                      // Every money figure is the one for the days this row COVERS: the engine's
                      // partial-period figure where it set one, the annual rate otherwise. A row that
                      // opens on 6 August showed a full year's pension and care fees next to a balance
                      // sheet that had only moved by the period's share — the columns disagreed with
                      // each other, and 2026 read almost identically to a full 2027.
                      const periodShare = year.isPartialFy ? (year.periodYearFraction ?? 1) : 1;
                      // The pension actually DEPOSITED across this row's days, not the rate the year
                      // opened on. They differ whenever the entitlement moves inside the year — a RAD
                      // paid on 1 December lifts it from that day, and a client reaching pension age in
                      // August is paid from their birthday. The engine re-assesses every payday and
                      // banks the re-assessed amount; this row used to report the opening rate instead,
                      // so the column and the money disagreed in exactly the years being modelled.
                      const agePension =
                          year.agePensionPaidForPeriod ?? year.socialSecurityForPartialPeriod ?? year.socialSecurity ?? 0;
                      const investmentIncome = year.investmentIncomeForPartialPeriod ?? year.investmentIncome ?? 0;
                      const fallbackInvLines = buildFallbackInvestmentIncomeLines(year, periodShare, formatCurrency);
                      // Rental income has no partial-period sibling on the row, so it is scaled here. It
                      // only ever reaches the fallback tooltip lines below (the ones used when the engine
                      // sent no `cashFlowBreakdown`), which must still add up to the headline.
                      const rentalIncome = (year.rentalIncome || 0) * periodShare;
                      // The recurring cost this module charges: aged care / Support at Home fees by
                      // default, village fees for a Retirement Village row (the engine pro-rates both
                      // to the period), nothing where the column is hidden.
                      const annualCareCosts = costSource === 'village'
                          ? ((year.annualVillageCost as number | undefined) ?? 0)
                          : (year.annualCareCostForPartialPeriod ?? year.annualCareCost ?? (agedCareCosts?.totalDailyCost || 0) * 365 * periodShare);
                      const livingExpenses = year.livingExpensesForPartialPeriod ?? year.livingExpenses ?? 0;
                      const tax = year.taxForPartialPeriod ?? year.tax ?? 0;
                      const netCashflow = year.cashFlowForPartialPeriod ?? year.cashFlow ?? 0;
  
                      return (
                        <React.Fragment key={`proj-row-${index}`}>
                        <tr className={`border-b border-slate-100 last:border-b-0 transition-colors hover:bg-sky-50/50 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/60'}`}>
                          {/* Every row is named the same way — its period, and when that period is a
                              partial one, the day it opened. The first row used to be headed
                              "Comparison Yr1" with the period demoted to a sub-line, which asked the
                              reader to learn a term before they could read the table. */}
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-slate-900">
                            {year.fyLabel ? (
                              <span title={year.periodStart && year.periodEnd ? `${year.periodStart.slice(0, 10)} → ${year.periodEnd.slice(0, 10)}` : undefined}>
                                {year.fyLabel}
                                {year.isPartialFy ? (
                                  <span className="block text-xs font-normal text-slate-500">
                                    {periodOpenedOn(year.periodStart) ? `from ${periodOpenedOn(year.periodStart)} ` : ''}(partial year)
                                  </span>
                                ) : null}
                              </span>
                            ) : (
                              <>Year {index + 1}</>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900 relative">
                            <CalculationTooltip title="Net Worth (Opening) Breakdown" data={deflateRows(createAssetBreakdown(index), index)}>
                              <span className={`flex items-center cursor-help font-mono ${moneyClass(index, '')}`}>
                                {formatCurrency(deflateAmount(openingBalance, index))}
                                <HelpCircle className="h-4 w-4 ml-1 text-gray-400" />
                              </span>
                            </CalculationTooltip>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm relative">
                            <CalculationTooltip
                              title="Age Pension Breakdown (estimate)"
                              data={deflateRows([
                                ...createAgePensionBreakdown(index, year.socialSecurity || 0),
                                ...pensionRateMovementLines(year, agePension, formatCurrency),
                              ], index)}
                            >
                              <span className={`flex items-center cursor-help font-mono ${moneyClass(index, agePension > 0 ? 'text-green-600' : 'text-gray-500')}`}>
                                {agePension > 0 ? `+${formatCurrency(deflateAmount(agePension, index))}` : formatCurrency(deflateAmount(agePension, index))}
                                <HelpCircle className="h-4 w-4 ml-1 text-gray-400" />
                              </span>
                            </CalculationTooltip>
                          </td>
                          {showCostColumn && (
                          <td className="px-4 py-3 whitespace-nowrap text-sm relative">
                            <CalculationTooltip
                              title={`${costHeading} Breakdown`}
                              data={deflateRows([
                                ...(year.agedCareBreakdown && year.agedCareBreakdown.length > 0
                                  ? year.agedCareBreakdown
                                  : [{ label: `Total ${costHeading} (annual rate)`, value: formatCurrency(costSource === 'village' ? annualCareCosts : (year.annualCareCost || 0)) }]),
                                ...(costSource === 'village' ? [] : periodScalingLines(year, year.annualCareCost || 0, annualCareCosts, formatCurrency)),
                              ], index)}
                            >
                              <span className={`flex items-center cursor-help font-mono ${moneyClass(index, 'text-red-600')}`}>
                                -{formatCurrency(deflateAmount(annualCareCosts, index))}
                                <HelpCircle className="h-4 w-4 ml-1 text-gray-400" />
                              </span>
                            </CalculationTooltip>
                          </td>
                          )}
                          <td className="px-4 py-3 whitespace-nowrap text-sm relative">
                            <CalculationTooltip
                              title="Net Cashflow Breakdown"
                              data={deflateRows((() => {
                                /*
                                 * The care fees leave the bank monthly from admission, outside the year's
                                 * income-and-spending lines, so they are listed here — at the same ANNUAL
                                 * rate as the lines above, which is what the year's cashflow nets them at,
                                 * so the tooltip adds up to its "Annual rate" line. Skipped only where the
                                 * engine's own lines already carry this module's cost (the village module's
                                 * fees). It used to skip on ANY line naming a village, which hid an aged
                                 * care resident's fees once village fees joined their lines (owner-caught,
                                 * 9 Oct 2026, on Bob Singletocare).
                                 */
                                const listed = (year.cashFlowBreakdown || []).reduce((t, item) => t + (typeof item.value === 'number' ? item.value : 0), 0);
                                const careAtAnnualRate = Math.max(0, listed - (Number(year.cashFlow) || 0));
                                const engineListsCost = (year.cashFlowBreakdown || []).some((item) => {
                                  const l = item.label.toLowerCase();
                                  return l.includes('aged care') || l.includes('care cost') || (costSource === 'village' && l.includes('village'));
                                });
                                const base = year.cashFlowBreakdown
                                  ? [
                                      ...year.cashFlowBreakdown.map(item => ({ label: item.label, value: item.value })),
                                      ...(!showCostColumn || annualCareCosts === 0 || engineListsCost || careAtAnnualRate < 1 ? [] : [
                                        { label: `--- ${costHeading.toUpperCase()} ---`, value: '' },
                                        { label: costSource === 'village' ? costHeading : `Less: ${costHeading} (paid monthly from the day care begins)`, value: -careAtAnnualRate }
                                      ])
                                    ]
                                  : [
                                      { label: '--- INCOME ---', value: '' },
                                      { label: 'Age Pension', value: '+' + formatCurrency(agePension) },
                                      ...fallbackInvLines,
                                      { label: 'Rental Income', value: '+' + formatCurrency(rentalIncome) },
                                      { label: '--- Total Income ---', value: '+' + formatCurrency(agePension + investmentIncome + rentalIncome) },
                                      { label: '--- EXPENSES ---', value: '' },
                                      ...(showCostColumn && annualCareCosts !== 0 ? [{ label: costHeading, value: '-' + formatCurrency(annualCareCosts) }] : []),
                                      { label: 'Living Expenses', value: '-' + formatCurrency(livingExpenses) },
                                      { label: 'Tax', value: '-' + formatCurrency(tax) },
                                      // Capital expenses are deliberately NOT scaled: a one-off either
                                      // falls inside the period or it does not, and the engine already
                                      // bands each one by the month it is pencilled in for.
                                      ...((year.capitalExpenses || 0) > 0 ? [{ label: 'Capital Expenses', value: '-' + formatCurrency(year.capitalExpenses || 0) }] : []),
                                      { label: '--- Total Expenses ---', value: '-' + formatCurrency(annualCareCosts + livingExpenses + tax + (year.capitalExpenses || 0)) },
                                      { label: '--- NET CASHFLOW ---', value: (netCashflow >= 0 ? '+' : '') + formatCurrency(netCashflow) }
                                    ];
                                const scaled = [...base, ...periodScalingLines(year, year.cashFlow || 0, netCashflow, formatCurrency)];
                                /*
                                 * "Surplus directed to ANZ" beneath a NEGATIVE net cashflow
                                 * (owner-caught, 2026-08-27: first three years of a do-nothing
                                 * projection).
                                 *
                                 * Not a calculation error - the closing net worth is right, and the
                                 * money does leave the bank. The two lines are struck on different
                                 * bases. Aged care is drawn from the bank MONTHLY by
                                 * agedCareCostPlugin, so cashflowPlugin deliberately settles the
                                 * year without it (see its own notes) and the figure it routes is
                                 * income less living costs - genuinely a surplus. The Net Cashflow
                                 * directly above INCLUDES care, so it is negative. The reader sees
                                 * a shortfall and is told there is a surplus.
                                 *
                                 * Relabelled here rather than in the engine because this is the one
                                 * place both figures are in hand, and the engine's routing is
                                 * correct about what it describes. Care did come out of that
                                 * account - monthly, which is exactly why no deficit waterfall ran.
                                 */
                                const routing = year.cashflowRoutingLine;
                                const misreadsAsSurplus =
                                  routing && netCashflow < 0 && /^Surplus directed to$/.test(String(routing.label));
                                const routingLine = misreadsAsSurplus
                                  ? { ...routing, label: 'Care drawn monthly from' }
                                  : routing;
                                return routingLine
                                  ? [...scaled, { label: '---', value: '' }, { ...routingLine }]
                                  : scaled;
                              })(), index)}
                            >
                              <span className={`flex items-center cursor-help font-mono ${moneyClass(index, netCashflow >= 0 ? 'text-green-600' : 'text-red-600')}`}>
                                {netCashflow >= 0 ? '+' : ''}{formatCurrency(deflateAmount(netCashflow, index))}
                                <HelpCircle className="h-4 w-4 ml-1 text-gray-400" />
                              </span>
                            </CalculationTooltip>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-900 relative">
                            <CalculationTooltip
                              title="Net Worth (Closing) Breakdown"
                              data={deflateRows(year.netWorthBreakdown
                                ? year.netWorthBreakdown.map(item => ({ label: item.label, value: item.value }))
                                : [
                                    { label: '--- OPENING POSITION ---', value: '' },
                                    { label: 'Opening Net Worth', value: formatCurrency(openingBalance) },
                                    { label: '--- GROWTH & INCOME ---', value: '' },
                                    ...fallbackInvLines,
                                    { label: 'Rental Income', value: '+' + formatCurrency(rentalIncome) },
                                    { label: 'Age Pension', value: '+' + formatCurrency(agePension) },
                                    { label: '--- EXPENSES ---', value: '' },
                                    { label: 'Aged Care Costs', value: '-' + formatCurrency(annualCareCosts) },
                                    { label: 'Living Expenses', value: '-' + formatCurrency(livingExpenses) },
                                    { label: 'Tax', value: '-' + formatCurrency(tax) },
                                    { label: '--- NET CHANGE ---', value: (closingBalance - openingBalance >= 0 ? '+' : '') + formatCurrency(closingBalance - openingBalance) },
                                    { label: '--- CLOSING NET WORTH ---', value: formatCurrency(closingBalance) }
                                  ], index)}
                            >
                              <span className={`flex items-center cursor-help font-mono ${moneyClass(index, '')}`}>
                                {formatCurrency(deflateAmount(closingBalance, index))}
                                <HelpCircle className="h-4 w-4 ml-1 text-gray-400" />
                              </span>
                            </CalculationTooltip>
                          </td>
                          <td className="px-2 py-3 text-right align-middle w-10">
                            {year.breakdown && year.breakdown.trim() ? (
                              <button
                                type="button"
                                className="inline-flex items-center justify-center rounded-md p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-200/70 transition-colors"
                                onClick={() => toggleBreakdown(index)}
                                aria-expanded={!!breakdownExpanded[index]}
                                aria-controls={`projection-breakdown-${index}`}
                                aria-label={breakdownExpanded[index]
                                  ? `Hide calculation breakdown for ${year.fyLabel || `Year ${index + 1}`}`
                                  : `Show calculation breakdown for ${year.fyLabel || `Year ${index + 1}`}`}
                                title={breakdownExpanded[index] ? 'Hide calculation breakdown' : 'Show calculation breakdown (detailed, illustrative)'}
                              >
                                {breakdownExpanded[index] ? (
                                  <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
                                ) : (
                                  <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
                                )}
                              </button>
                            ) : null}
                          </td>
                        </tr>
                        {year.breakdown && year.breakdown.trim() && breakdownExpanded[index] ? (
                          <tr className="border-b border-slate-100 bg-slate-50/40">
                            {/* No padding here and no frame on the breakdown itself: it reads as part of
                                the table it belongs to rather than as a card dropped into a row. Its own
                                horizontal padding matches the cells above, so the text lines up. */}
                            <td colSpan={showCostColumn ? 7 : 6} className="p-0">
                              <div
                                id={`projection-breakdown-${index}`}
                                role="region"
                                aria-label={`Calculation breakdown for ${year.fyLabel || `Year ${index + 1}`}`}
                                className={`${PROJECTION_BREAKDOWN_CLASS} max-h-96 overflow-y-auto px-4 py-3`}
                                dangerouslySetInnerHTML={{ __html: renderBreakdownHtml(year.breakdown, index) }}
                              />
                            </td>
                          </tr>
                        ) : null}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          {/* Covers the charts AND the table, both of which are redrawn from the incoming rows. The
              previous period's figures stay visible underneath rather than the modal emptying out. */}
          {isReloading ? (
            <div className="absolute inset-0 z-10 flex items-start justify-center bg-white/70 pt-16" role="status" aria-live="polite">
              <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 shadow-md">
                <Hourglass className="h-4 w-4 shrink-0 animate-pulse text-teal-600" aria-hidden />
                <span className="text-sm font-medium text-slate-700">Recalculating by {periodNoun}…</span>
              </div>
            </div>
          ) : null}
        </div>
    </>
  );
};

/**
 * The manual-control overlay: full-screen chrome around the shared {@link ProjectionsView}. The
 * Suggestion Engine deep dive embeds the view directly inside its own tabbed modal instead.
 */
const ProjectionsModal = (props: ProjectionsModalProps) => createPortal(
  <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
    {/* Wide by design: a decade of bars across the chart accordion, and a table whose six money
        columns don't compress below 900px, both want the room a large screen has. Still `w-full`
        with the backdrop's padding, so a small window is unaffected.

        Portalled to the body because this modal is now opened from EVERY module page, and several
        render it inside the shared Card — whose `backdrop-blur-xl` establishes a containing block
        that `position: fixed` resolves against. Without the portal the overlay is imprisoned in the
        card's column, exactly the fault the suggestion engine's Deep Dive had. */}
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-screen-2xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-200/80">
      <ProjectionsView {...props} />
    </div>
  </div>,
  document.body,
);

export default ProjectionsModal;
